import { useState, useEffect, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import { modalCargando, mensajeEstatico } from "../../../components/ui/sweetAlerts";
import { createFetchTableData } from "../../../utils/createFetchTableData";
import { post } from "../../../api/apis";

export const useMonitor = (proveedor) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [refreshToggle, setRefreshToggle] = useState(0);

  const initialSortBy = "IdLote";
  const initialSortDir = "desc";

  const recargarTabla = useCallback(() => {
    setRefreshToggle(prev => prev + 1);
    setSelectedRows([]);
  }, []);

  const fetchTableData = useMemo(
    () =>
      createFetchTableData({
        url: "monitor/contratos",
        initialSortBy,
        initialSortDir,
        extraParams: { proveedor: proveedor },
        dataPath: ["data", "data", "data"],
        totalPath: ["data", "data", "total"],
      }),
    [proveedor],
  );

  // Manejar doble clic en una fila para abrir el modal
  const handleRowDoubleClick = rowData => {
    setSelectedRow(rowData);
    setShowModal(true);
  };

  const handleKeyDown = e => {
    if (e.key === "Escape") {
      setShowModal(false);
    }
  };

  const handleClickOutside = e => {
    const modalContent = document.querySelector(".modal-content");
    if (modalContent && !modalContent.contains(e.target)) {
      setShowModal(false);
    }
  };

  const reenviarContrato = async id => {
    try {
      modalCargando("Reenviando");
      const resp = await post("reenvio_contrato", { id_contrato: id });
      if (resp.data.status === "info") {
        mensajeEstatico("info", "Atención", resp.data.message);
      } else if (resp.data.status === "success") {
        mensajeEstatico("success", "Éxito", resp.data.message);
        recargarTabla();
      } else {
        mensajeEstatico("error", "Error", "Ha ocurrido un error.");
      }
    } catch (error) {
      console.error("Error al reenviar contrato:", error);
      mensajeEstatico("error", `No se pudo reenviar el contrato ${id}.`);
    }
  };

  // --- ACCIÓN: Cancelar en Webdox (Individual o Masivo) ---
  const cancelarEnWebdox = async ({ ids, id_wf, motivo = "Nuevo flujo de documentos" }) => {
    const rawArray = Array.isArray(ids) ? ids : [ids];
    const idsArray = rawArray.map(r => (typeof r === "object" && r !== null ? r.id : r)).filter(id => id !== undefined && id !== null && id !== "");
    if (idsArray.length === 0) {
      mensajeEstatico("warning", "Atención", "Debe seleccionar al menos un contrato.");
      return;
    }

    const { value: confirmacion } = await Swal.fire({
      title: "¿Cancelar en Webdox?",
      text: `Se cancelará el flujo de firma en Webdox para ${idsArray.length} contrato(s).`,
      icon: "warning",
      input: "text",
      inputPlaceholder: "Ingrese motivo de cancelación (opcional)",
      inputValue: motivo,
      showCancelButton: true,
      confirmButtonText: "Sí, Cancelar",
      cancelButtonText: "Atrás",
      focusCancel: true,
      buttonsStyling: false,
      customClass: {
        popup: "p-4 border-0",
        title: "fw-bold fs-5 text-dark mt-2 mb-1",
        htmlContainer: "text-muted fs-6 mb-3",
        input: "form-control rounded-pill text-center shadow-none mb-3",
        confirmButton: "btn btn-danger px-4 py-2 rounded-pill fw-bold text-white me-2",
        cancelButton: "btn btn-secondary px-4 py-2 rounded-pill fw-bold",
      },
      didOpen: popup => {
        popup.style.borderRadius = "16px";
        popup.style.border = "1px solid #cbd5e1";
        popup.style.boxShadow = "none";
      },
    });

    if (confirmacion !== undefined) {
      try {
        modalCargando("Cancelando en Webdox...");
        const payload = {
          id_contrato: idsArray,
          id_wf: id_wf || "",
          motivo: confirmacion || "Nuevo flujo de documentos",
        };
        const resp = await post("firma/webdox/cancelar", null, payload);
        if (resp.data.status === "success") {
          mensajeEstatico("success", "Cancelado con éxito", resp.data.message || "Proceso cancelado en Webdox.");
          setShowModal(false);
          recargarTabla();
        } else {
          mensajeEstatico("error", "Error", resp.data.message || "No se pudo cancelar el proceso.");
        }
      } catch (error) {
        console.error("Error al cancelar en Webdox:", error);
        const errMsg = error?.response?.data?.message || "Ocurrió un error al procesar la cancelación en Webdox.";
        mensajeEstatico("error", "Error", errMsg);
      }
    }
  };

  // --- ACCIÓN: Reenviar a Confírmalo (Individual o Masivo) ---
  const reenviarAConfirmalo = async ({ ids, id_wf, motivo = "Reenvío hacia Confírmalo" }) => {
    const rawArray = Array.isArray(ids) ? ids : [ids];
    const idsArray = rawArray.map(r => (typeof r === "object" && r !== null ? r.id : r)).filter(id => id !== undefined && id !== null && id !== "");
    if (idsArray.length === 0) {
      mensajeEstatico("warning", "Atención", "Debe seleccionar al menos un contrato.");
      return;
    }

    const { isConfirmed } = await Swal.fire({
      title: "¿Reenviar a Confírmalo?",
      text: `Se cancelará en Webdox y se iniciará un nuevo flujo de firma en Confírmalo para ${idsArray.length} contrato(s).`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, Reenviar a Confírmalo",
      cancelButtonText: "Cancelar",
      focusCancel: true,
      buttonsStyling: false,
      customClass: {
        popup: "p-4 border-0",
        title: "fw-bold fs-5 text-dark mt-2 mb-1",
        htmlContainer: "text-muted fs-6 mb-3",
        confirmButton: "btn px-4 py-2 rounded-pill fw-bold text-white me-2",
        cancelButton: "btn btn-secondary px-4 py-2 rounded-pill fw-bold",
      },
      didOpen: popup => {
        popup.style.borderRadius = "16px";
        popup.style.border = "1px solid #cbd5e1";
        popup.style.boxShadow = "none";
        const confirmBtn = popup.querySelector(".swal2-confirm");
        if (confirmBtn) {
          confirmBtn.style.backgroundColor = "#2a77b9";
          confirmBtn.style.borderColor = "#2a77b9";
        }
      },
    });

    if (isConfirmed) {
      try {
        modalCargando("Iniciando flujo en Confírmalo...");
        const payload = {
          id_contrato: idsArray,
          id_wf: id_wf || "",
          motivo,
        };
        const resp = await post("firma/webdox/reenviar-confirmalo", null, payload);
        if (resp.data.status === "success") {
          mensajeEstatico("success", "Reenvío exitoso", resp.data.message || "Solicitud enviada a Confírmalo.");
          setShowModal(false);
          recargarTabla();
        } else {
          mensajeEstatico("error", "Error", resp.data.message || "No se pudo reenviar a Confírmalo.");
        }
      } catch (error) {
        console.error("Error al reenviar a Confírmalo:", error);
        const errMsg = error?.response?.data?.message || "Ocurrió un error al procesar el reenvío a Confírmalo.";
        mensajeEstatico("error", "Error", errMsg);
      }
    }
  };

  // Cerrar modal al presionar Escape o clic fuera del modal
  useEffect(() => {
    if (!showModal) return;

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showModal]);

  return {
    handleKeyDown,
    handleClickOutside,
    handleRowDoubleClick,
    showModal,
    selectedRow,
    setShowModal,
    reenviarContrato,
    fetchTableData,
    selectedRows,
    setSelectedRows,
    refreshToggle,
    recargarTabla,
    cancelarEnWebdox,
    reenviarAConfirmalo,
  };
};